/**
 * TryDemo — public, shareable "Try the demo" landing page (/try-demo).
 * Captures the visitor as a lead, spins up an isolated 1-hour demo workspace
 * (POST /api/demo/start), logs them straight in and drops them on the dashboard.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowRight, Loader2, Building2, Clock, ShieldCheck, Sparkles,
} from 'lucide-react';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';
const API = process.env.REACT_APP_BACKEND_URL;

const AUDIENCES = [
  { key: 'builder', label: 'Builder / Developer' },
  { key: 'bank', label: 'Bank / Lender' },
  { key: 'creator', label: 'Content creator' },
  { key: 'other', label: 'Just curious' },
];

export default function TryDemo({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === '1';
  const [form, setForm] = useState({ name: '', email: '', company: '', audience: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form'); // 'form' | 'code'
  const [code, setCode] = useState('');

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Step 1 — email a verification code (so we only capture real, reachable leads).
  const sendCode = async () => {
    setError('');
    if (!form.name.trim()) { setError('Please enter your name.'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { setError('Please enter a valid email.'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/demo/request-code`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        audience: form.audience,
      });
      setStep('code');
      setCode('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send the code. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Step 2 — verify the code, then launch the demo.
  const verifyAndStart = async () => {
    setError('');
    if (!/^\d{6}$/.test(code.trim())) { setError('Enter the 6-digit code from your email.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/demo/verify-code`, {
        email: form.email.trim(),
        code: code.trim(),
      });
      const data = res.data;
      // Persist the Bearer session token — demo API calls authenticate with it
      // (the demo endpoint sets no cookie), otherwise the dashboard 401s and
      // the visitor is booted back to /login.
      if (data.session_token) {
        localStorage.setItem('dr_session_token', data.session_token);
      }
      if (data.demo_session) {
        localStorage.setItem('demo_session', JSON.stringify(data.demo_session));
      }
      localStorage.setItem('demo_lead', JSON.stringify({ name: form.name.trim(), email: form.email.trim() }));
      localStorage.setItem('user', JSON.stringify(data));
      if (onLogin) onLogin(data);
      navigate('/dashboard');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not verify the code. Please try again.');
      setSubmitting(false);
    }
  };

  // Post-demo conversion hand-off: after the 1-hour demo ends we push the
  // visitor to create a real account (30-day free Pro trial) rather than just
  // restarting the demo. Pre-filled from the captured lead.
  let lead = {};
  try { lead = JSON.parse(localStorage.getItem('demo_lead') || '{}'); } catch { lead = {}; }
  const goSignup = () => {
    const qs = new URLSearchParams({ from: 'demo', discount: 'DEMO30' });
    if (lead.email) qs.set('email', lead.email);
    if (lead.name) qs.set('name', lead.name);
    // Clear any lingering demo session first — otherwise the /signup route guard
    // treats the demo user as authenticated and bounces them to /dashboard.
    // A hard navigation re-initializes app state with no user.
    try {
      localStorage.removeItem('demo_session');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    } catch { /* ignore */ }
    window.location.href = `/signup?${qs.toString()}`;
  };

  return (
    <div className="min-h-screen flex items-stretch" style={{ background: `linear-gradient(135deg, ${NAVY}, #0f2f56)` }} data-testid="try-demo-page">
      <div className="max-w-5xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-8 items-center px-5 py-10">
        {/* Left — pitch */}
        <div className="text-white">
          <img
            src="/logo-demo-white.png"
            alt="DrawReady"
            className="h-12 w-auto mb-6"
            data-testid="try-demo-logo"
          />
          <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight">Take DrawReady for a spin — no signup.</h1>
          <p className="text-blue-100 mt-3 text-base max-w-md">You&apos;ll get your own private workspace pre-loaded with real sample projects: budgets, draw requests, contractor bids and bank-ready PDFs. Click anything.</p>
          <div className="mt-6 space-y-3 text-sm">
            <div className="flex items-center gap-3"><Sparkles className="w-4 h-4" style={{ color: '#a8d977' }} /> Full playground — create projects, bids &amp; draws</div>
            <div className="flex items-center gap-3"><ShieldCheck className="w-4 h-4" style={{ color: '#a8d977' }} /> Private &amp; isolated — only you see your demo data</div>
            <div className="flex items-center gap-3"><Clock className="w-4 h-4" style={{ color: '#a8d977' }} /> Free for 1 hour, then it resets</div>
          </div>
        </div>

        {/* Right — lead form OR post-demo conversion hand-off */}
        {expired ? (
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8" data-testid="demo-converted-panel">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: '#eaf3e0' }}>
              <Sparkles className="w-6 h-6" style={{ color: GREEN }} />
            </div>
            <h2 className="text-2xl font-extrabold text-slate-900">
              {lead.name ? `Nice work, ${lead.name.split(' ')[0]}.` : 'Your demo has ended.'}
            </h2>
            <p className="text-sm text-slate-600 mt-2">
              That was the real DrawReady — now make it yours. Create a free account and keep every project,
              bid and draw you set up, with a <b>30-day free Pro trial</b> — plus <b>30% off your first 3 months</b>.
              No card charged today.
            </p>
            <div className="mt-5 space-y-2.5 text-sm text-slate-700">
              <div className="flex items-center gap-3"><ShieldCheck className="w-4 h-4" style={{ color: GREEN }} /> 30 days of full Pro features, free</div>
              <div className="flex items-center gap-3"><Sparkles className="w-4 h-4" style={{ color: GREEN }} /> Then 30% off your first 3 months</div>
              <div className="flex items-center gap-3"><Building2 className="w-4 h-4" style={{ color: GREEN }} /> Bring in your own projects &amp; contractors</div>
            </div>
            <button
              onClick={goSignup}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 text-white font-bold text-sm px-5 py-3.5 rounded-xl hover:shadow-lg transition"
              style={{ background: GREEN }}
              data-testid="demo-signup-cta"
            >
              Create my free account <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => { window.location.href = '/try-demo'; }}
              className="mt-3 w-full text-sm font-semibold text-slate-500 hover:text-slate-800"
              data-testid="demo-restart-btn"
            >
              Or run the 1-hour demo again
            </button>
          </div>
        ) : (
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8" data-testid="try-demo-form">
          {step === 'form' ? (
          <>
          <h2 className="text-xl font-extrabold text-slate-900">Start your free demo</h2>
          <p className="text-sm text-slate-500 mt-1">We&apos;ll email you a quick code to verify it&apos;s you.</p>
          <div className="space-y-3 mt-5">
            <div>
              <label className="text-xs font-semibold text-slate-500">Name</label>
              <input value={form.name} onChange={upd('name')} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="try-demo-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Work email</label>
              <input value={form.email} onChange={upd('email')} type="email" className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="try-demo-email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Company (optional)</label>
              <div className="mt-1 relative">
                <Building2 className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input value={form.company} onChange={upd('company')} className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="try-demo-company" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">I&apos;m a…</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {AUDIENCES.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, audience: a.key }))}
                    data-testid={`try-demo-audience-${a.key}`}
                    className={`text-xs font-semibold rounded-lg px-3 py-2 border transition ${form.audience === a.key ? 'text-white border-transparent' : 'text-slate-600 border-slate-300 hover:border-slate-400'}`}
                    style={form.audience === a.key ? { background: NAVY } : {}}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            {error && <p className="text-sm text-red-600" data-testid="try-demo-error">{error}</p>}
            <button
              onClick={sendCode}
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 text-white font-bold text-sm px-5 py-3.5 rounded-xl disabled:opacity-60 hover:shadow-lg transition"
              style={{ background: GREEN }}
              data-testid="try-demo-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
              {submitting ? 'Sending your code…' : 'Send me a code'}
            </button>
            <p className="text-[11px] text-slate-400 text-center">By continuing you agree to be contacted about DrawReady.</p>
          </div>
          </>
          ) : (
          <div data-testid="try-demo-code-step">
            <button onClick={() => { setStep('form'); setError(''); }} className="text-xs font-semibold text-slate-400 hover:text-slate-700 mb-3" data-testid="try-demo-change-email">← Use a different email</button>
            <h2 className="text-xl font-extrabold text-slate-900">Check your email</h2>
            <p className="text-sm text-slate-500 mt-1">
              We sent a 6-digit code to <b className="text-slate-800">{form.email.trim()}</b>. Enter it below to launch your demo.
            </p>
            <div className="space-y-3 mt-5">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                className="w-full border border-slate-300 rounded-lg px-3 py-3 text-center text-2xl font-bold tracking-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="try-demo-code-input"
              />
              {error && <p className="text-sm text-red-600" data-testid="try-demo-error">{error}</p>}
              <button
                onClick={verifyAndStart}
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 text-white font-bold text-sm px-5 py-3.5 rounded-xl disabled:opacity-60 hover:shadow-lg transition"
                style={{ background: GREEN }}
                data-testid="try-demo-verify"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {submitting ? 'Spinning up your demo…' : 'Verify & launch demo'}
              </button>
              <button onClick={sendCode} disabled={submitting} className="w-full text-xs font-semibold text-slate-500 hover:text-slate-800 disabled:opacity-50" data-testid="try-demo-resend">
                Didn&apos;t get it? Resend code
              </button>
            </div>
          </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
